import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { generatePath, Outlet, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import useSteps, { IReservation } from "@/pages/reservations/hooks/useSteps";
import { AppBar, IconButton, Stack, Toolbar, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import routes from "@/constants/routes";
import { useContext, useEffect, useMemo } from "react";
import useUserProfile from "@/hooks/useUserProfile";
import { Organization, User } from "@/types";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import useRoutes from "@/hooks/useRoutes";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import useSendThroughWhatsapp from "@/pages/reservations/hooks/useSendThroughWhatsapp";

export type ReservationSharedProps = {
  activeUser: User | null;
  organization?: Organization | null;
};

type Params = {
  organizationAlias: string | undefined;
  orderId: string | undefined;
};

const ReservationsPage = () => {
  const [navigate] = useRoutes();
  const { activeUser } = useUserProfile();
  const { organization } = useContext(WebsiteContext);
  const sendThroughWhatsapp = useSendThroughWhatsapp();
  const { organizationAlias, orderId } = useParams<Params>();
  const { stepId, steps, previousStep, nextStep, isFirstStep, isLastStep, initialValues, validationSchema, activeStep } = useSteps();

  const sharedProps: ReservationSharedProps = useMemo(() => {
    return { activeUser, organization };
  }, [activeUser, organization]);

  const onSubmit = async (values: IReservation) => {
    if (isLastStep && validationSchema?.isValid(values)) {
      await sendThroughWhatsapp(values);
    }

    // execute step tasks
    if (activeStep?.onSubmit) {
      activeStep.onSubmit(values, sharedProps);
    }

    // continue
    navigate("organizations.reservations.form", { formId: nextStep.id });
  };

  const onBack = () => {
    if (isFirstStep) {
      navigate("organizations.menu.main", {}, { replace: true });
    } else {
      navigate("organizations.reservations.form", { formId: previousStep.id });
    }
  };

  useEffect(() => {
    if (stepId === "checkout") {
      navigate(
        generatePath(routes.organizations.order.checkoutForm, {
          organizationAlias,
          orderId,
          formId: steps[0].id,
        }) + location.search
      );
    }
  }, [navigate, orderId, organizationAlias, stepId, steps]);
  
  if (!activeStep) {
    return <p style={{ color: "white" }}>Loading data...</p>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack
        sx={{
          height: "100dvh",
          backgroundColor: "#DDEAFD",
          "& form": {
            height: "100%",
            display: "flex",
          },
        }}
      >
        <AppBar position="static" color="default" elevation={2}>
          <Toolbar sx={{ py: 1.5 }}>
            <IconButton size="large" edge="start" color="inherit" aria-label="menu" sx={{ ml: -0.5, mr: 1 }} onClick={onBack}>
              <HugeiconsIcon icon={ArrowLeft01Icon} />
            </IconButton>
            <Typography variant="h3">{activeStep.title}</Typography>
          </Toolbar>
        </AppBar>
        <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema} enableReinitialize={true}>
          {() => {
            return (
              <Form>
                <Stack flex={1} padding={3} spacing={2} justifyContent="space-between">
                  <Outlet context={{ steps }} />
                  <LoadingButton size="large" type="submit" endIcon={!isLastStep && <HugeiconsIcon icon={ArrowRight01Icon} />} variant="contained" fullWidth color={isLastStep ? "success" : "primary"}>
                    {isLastStep ? "Finalizar" : "Continuar"}
                  </LoadingButton>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Stack>
    </LocalizationProvider>
  );
};

export default ReservationsPage;
